import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export const UPLOAD_FAILED = Symbol('upload-failed');

export function isolateUploads(uploads$: Observable<unknown>[]): Observable<unknown>[] {
  return uploads$.map(upload$ => upload$.pipe(catchError(() => of(UPLOAD_FAILED))));
}

export function runUploads(uploads$: Observable<unknown>[]): Observable<unknown[]> {
  return forkJoin(isolateUploads(uploads$));
}

export function countUploadFailures(results: unknown[]): number {
  return results.filter(result => result === UPLOAD_FAILED).length;
}

/**
 * A failed file action, kept as a value so the round can finish and the operator can still be told
 * *why* it failed. `isolateUploads` throws the error away, which is fine for a plain upload round
 * but not for the wizard's save: the server's per-kind cap refusal has to reach
 * `ToastService.showRequestError` to be named (LNA-1850).
 */
export interface FailedFileAction {
  readonly failed: typeof FILE_ACTION_FAILED;
  readonly error: unknown;
}

export const FILE_ACTION_FAILED = Symbol('file-action-failed');

export function isFailedFileAction(result: unknown): result is FailedFileAction {
  return !!result && typeof result === 'object' && (result as FailedFileAction).failed === FILE_ACTION_FAILED;
}

/** Lets every action in the round run to completion instead of `forkJoin` cancelling the siblings. */
export function isolateFileActions(actions$: Observable<unknown>[]): Observable<unknown>[] {
  return actions$.map(action$ => action$.pipe(catchError((error: unknown) => of<FailedFileAction>({ failed: FILE_ACTION_FAILED, error }))));
}

export function countFileActionFailures(results: unknown[]): number {
  return results.filter(isFailedFileAction).length;
}

export function firstFileActionError(results: unknown[]): unknown {
  return results.find(isFailedFileAction)?.error;
}

/**
 * Runs `first$` to completion before `then$` starts, as a single observable. Every action runs even
 * when one is refused; the round fails afterwards with the first refusal, so a host still learns
 * that it failed. A host's own `isolateFileActions` sees only this outer observable, which is why
 * the isolation has to happen inside: without it one refused update cancelled its siblings (LNA-2047).
 */
export function runFileActionsInOrder(first$: Observable<unknown>[], then$: Observable<unknown>[]): Observable<unknown[]> {
  return settle(first$).pipe(
    switchMap(firstResults => settle(then$).pipe(map(thenResults => [...firstResults, ...thenResults]))),
    switchMap(results => (countFileActionFailures(results) > 0 ? throwError(() => firstFileActionError(results)) : of(results)))
  );
}

// forkJoin([]) completes without emitting, which would leave the round hanging.
function settle(actions$: Observable<unknown>[]): Observable<unknown[]> {
  return actions$.length === 0 ? of([]) : forkJoin(isolateFileActions(actions$));
}
